import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import './modal-shell.less'
import { Modal, ModalContent } from '@mytours/components'
import { isDesktopDevice } from 'mobile_web/modules/platform/device'

export interface ModalShellProps {
	isOpen: boolean
	// `popover` is the iOS popover the side menu uses; it takes focus only once its entrance has run.
	variant?: 'ios' | 'popover'
	className?: string
	backdropDismiss?: boolean
	showBackdrop?: boolean
	children?: React.ReactNode
	onDidPresent?: () => void
	onWillDismiss?: () => void
	onDidDismiss?: () => void
	'data-cy'?: string
}

// Each modal takes the next stacking level when it is created, so one mounted later sits above.
let lastZIndex = 20000
const nextZIndex = () => ++lastZIndex

// The library's iOS modal, mounted in <ion-app>; a visitor's dismissal holds until `isOpen` next turns true.
const ModalShell: React.FC<ModalShellProps> = ({
	isOpen,
	variant = 'ios',
	className,
	backdropDismiss = true,
	showBackdrop = true,
	children,
	onDidPresent,
	onWillDismiss,
	onDidDismiss,
	'data-cy': dataCy,
}) => {
	const [zIndex] = useState(nextZIndex)
	const [dismissed, setDismissed] = useState(false)
	// <ion-app> is not in the document yet on the first render; the portal waits for it.
	const [container, setContainer] = useState<HTMLElement | null>(null)
	useLayoutEffect(() => setContainer(document.querySelector<HTMLElement>('ion-app') ?? document.body), [])
	const open = isOpen && !dismissed
	const wasOpen = useRef(open)
	const popupRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!isOpen) setDismissed(false)
	}, [isOpen])

	useEffect(() => {
		if (wasOpen.current && !open) onWillDismiss?.()
		wasOpen.current = open
	}, [open, onWillDismiss])

	return (
		<Modal
			variant={variant}
			open={open}
			disablePointerDismissal={!backdropDismiss}
			onOpenChange={next => {
				if (!next && backdropDismiss) setDismissed(true)
			}}
			onOpenChangeComplete={presented => {
				if (presented && variant === 'popover') popupRef.current?.focus({ preventScroll: true })
				return presented ? onDidPresent?.() : onDidDismiss?.()
			}}
		>
			<ModalContent
				container={container}
				transparentBackdrop={!showBackdrop}
				desktop={variant === 'popover' && isDesktopDevice()}
				ref={popupRef}
				{...(variant === 'popover' && { initialFocus: false })}
				hostProps={{
					className: classNames(className, { 'backdrop-no-dismiss': !backdropDismiss }),
					id: `modal-${zIndex}`,
					style: { zIndex },
					'data-cy': dataCy,
				}}
			>
				{variant === 'popover' ? (
					<div className="popover-viewport">{children}</div>
				) : (
					<div className="modal-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
						{children}
					</div>
				)}
			</ModalContent>
		</Modal>
	)
}

export default ModalShell
